// 3rd
// internal
import { TypeAssert } from '@creejs/commons-lang'

// owned
import { DefaultOwner } from './constants.js'

// module vars
const { assertFunction, assertNotNil } = TypeAssert
/**
 * Wraps a function to be called when an event is fired.
 * @typedef {import('./event.js').default} Event
 * @class Listener
 */
export default class Listener {
  /**
   * @param {Event} event
   * @param {function} callback - The function to be called when event is fired
   * @param {boolean} [isOnce=false] - is a one time listener?
   */
  constructor (event, callback, isOnce = false) {
    assertNotNil(event, 'event')
    assertFunction(callback, 'callback')
    this._event = event
    this._callback = callback
    this._isOnce = !!isOnce // is Once Listener?
    this._owner = undefined
  }

  /**
   * Sets the owner of this listener.
   * @param {*} owner - The owner object to be associated with this listener.
   */
  set owner (owner) {
    this._owner = owner
  }

  get owner () {
    return this._owner === DefaultOwner ? undefined : this._owner
  }

  get event () {
    return this._event
  }

  get isOnce () {
    return this._isOnce
  }

  /**
   * Checks if the provided function is the same as the listener's wrapped function.
   * @param {Function} callback - The function to compare against.
   * @returns {boolean} True if the functions are the same, false otherwise.
   */
  isSameCallback (callback) {
    return this._callback === callback
  }

  get callback () {
    return this._callback
  }

  /**
   * Invokes the stored function with the provided arguments.
   * @param {...*} args - Arguments to pass to the function.
   * @returns {*} The result of the function invocation.
   */
  invoke (...args) {
    try {
      return this._callback(...args)
    } finally {
      if (this._isOnce) {
        try {
          this._event._remove(this)
        } catch (err) {
          // do nothing
          console.warn(err)
        }
      }
    }
  }

  /**
   * Invokes the listener with the provided arguments.
   * Alias for {@linkcode Listener.invoke}
   * @param {...*} args - Arguments to be passed to the listener.
   * @returns {*} The result of the listener invocation.
   */
  listener (...args) {
    return this.invoke(...args)
  }
}
export { Listener as ListenerType }
